/**
 * editorHistoryStore — immer produceWithPatches 기반 로컬 undo/redo 스토어.
 *
 * 기존 zundo(store.ts)와 공존하며, 별도의 경량 스토어로 에디터 변경 이력을 관리한다.
 * EditorPage에서 직접 호출해 변경 사항을 기록하고, HistoryPanel이 이를 구독한다.
 *
 * 사용 패턴:
 *   1. printUnit/albumInfo 변경 직후 recordChange('updatePrintUnit') 호출
 *   2. HistoryPanel에서 past 목록 표시
 *   3. 항목 클릭 → jumpTo(index) 로 해당 시점 상태 복원
 */

import { create } from 'zustand';
import { INITIAL_HISTORY, type HistoryState, type HistoryEntry } from './immerHistory';

/** 히스토리 항목에서 UI에 표시할 한국어 라벨 매핑 */
export const HISTORY_LABEL_MAP: Record<string, string> = {
  setAlbumInfo: '앨범 정보 변경',
  setTracklist: '트랙리스트 변경',
  addTrack: '트랙 추가',
  updateTrack: '트랙 수정',
  removeTrack: '트랙 삭제',
  reorderTrack: '트랙 순서 변경',
  updatePrintUnit: '인쇄부 설정 변경',
  updateBackground: '배경 변경',
  updateText: '텍스트 설정 변경',
  updateCorner: '모서리 설정 변경',
  updateFilter: '필터 변경',
  updateLogo: '로고 설정 변경',
  applyClipboard: '스타일 붙여넣기',
};

/** 라벨로 한국어 표시명 반환 */
export function getHistoryLabelDisplay(label: string): string {
  return HISTORY_LABEL_MAP[label] ?? label;
}

export interface EditorHistoryStoreState {
  /** 히스토리 스택 (past / future) */
  history: HistoryState;

  /** 변경 항목을 수동으로 히스토리에 등록 */
  pushEntry: (entry: Omit<HistoryEntry, 'timestamp'>) => void;

  /** 라벨만으로 빈 항목 기록 (스냅샷 없이 타임라인 표시 전용) */
  recordChange: (label: string) => void;

  /** undo: past에서 항목 꺼내 future로 이동 */
  undo: () => HistoryEntry | null;

  /** redo: future에서 항목 꺼내 past로 이동 */
  redo: () => HistoryEntry | null;

  /** 특정 인덱스(past 배열 기준)로 점프 */
  jumpTo: (index: number) => void;

  /** 히스토리 전체 초기화 */
  clearHistory: () => void;

  canUndo: boolean;
  canRedo: boolean;
}

const MAX_DEPTH = 50;

export const useEditorHistoryStore = create<EditorHistoryStoreState>()((set, get) => ({
  history: INITIAL_HISTORY,
  canUndo: false,
  canRedo: false,

  pushEntry: (entry) => {
    set((state) => {
      const newEntry: HistoryEntry = { ...entry, timestamp: Date.now() };
      const nextPast = [...state.history.past, newEntry];
      const trimmed =
        nextPast.length > MAX_DEPTH ? nextPast.slice(nextPast.length - MAX_DEPTH) : nextPast;
      const newHistory: HistoryState = { past: trimmed, future: [] };
      return {
        history: newHistory,
        canUndo: trimmed.length > 0,
        canRedo: false,
      };
    });
  },

  recordChange: (label) => {
    const entry: HistoryEntry = {
      patches: [],
      inversePatches: [],
      label,
      timestamp: Date.now(),
    };
    set((state) => {
      const nextPast = [...state.history.past, entry];
      const trimmed =
        nextPast.length > MAX_DEPTH ? nextPast.slice(nextPast.length - MAX_DEPTH) : nextPast;
      const newHistory: HistoryState = { past: trimmed, future: [] };
      return {
        history: newHistory,
        canUndo: trimmed.length > 0,
        canRedo: false,
      };
    });
  },

  undo: () => {
    const { history } = get();
    const past = history.past;
    if (past.length === 0) return null;
    const entry = past[past.length - 1];
    if (!entry) return null;
    set((state) => {
      const newPast = state.history.past.slice(0, -1);
      const newFuture = [...state.history.future, entry];
      return {
        history: { past: newPast, future: newFuture },
        canUndo: newPast.length > 0,
        canRedo: true,
      };
    });
    return entry;
  },

  redo: () => {
    const { history } = get();
    const future = history.future;
    if (future.length === 0) return null;
    const entry = future[future.length - 1];
    if (!entry) return null;
    set((state) => {
      const newFuture = state.history.future.slice(0, -1);
      const newPast = [...state.history.past, entry];
      return {
        history: { past: newPast, future: newFuture },
        canUndo: true,
        canRedo: newFuture.length > 0,
      };
    });
    return entry;
  },

  jumpTo: (index) => {
    set((state) => {
      const past = state.history.past;
      if (index < 0 || index >= past.length) return {};
      // index 이후 항목을 future로 이동
      const newPast = past.slice(0, index + 1);
      const movedToFuture = past.slice(index + 1).reverse();
      return {
        history: { past: newPast, future: [...state.history.future, ...movedToFuture] },
        canUndo: newPast.length > 0,
        canRedo: (movedToFuture.length + state.history.future.length) > 0,
      };
    });
  },

  clearHistory: () => {
    set({
      history: INITIAL_HISTORY,
      canUndo: false,
      canRedo: false,
    });
  },
}));
